import React from 'react';
import classnames from 'classnames';
import { Checkbox, NumericInput, Popup, PropertyGrid, TabPane, Text } from '@leverege/ui-elements';
import { useValueChange } from '@leverege/ui-hooks';
import I18N from '@leverege/i18n';
import { ModelTypeSelector } from '@leverege/ui-plugin';
import { Colorizers } from '@leverege/ui-attributes';
import Model from "./GroupClusterModel.js";
import S from './Grid.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t,
  tIcon
} = I18N.ns('molten.dataViewers.googleMap.GroupClusterEditor');
export default function GroupClusterEditor(props) {
  const {
    value
  } = props;
  const onModelChange = useValueChange(Model, props);
  const objectType = Model.getObjectType(value);
  return /*#__PURE__*/_jsxs(TabPane, {
    children: [/*#__PURE__*/_jsx(TabPane.Tab, {
      tabName: t('visibility'),
      tabId: "vis",
      children: /*#__PURE__*/_jsxs(PropertyGrid, {
        variant: "groupClusterEditor|editor",
        children: [/*#__PURE__*/_jsx(PropertyGrid.Item, {
          fillCell: true,
          label: /*#__PURE__*/_jsx(Checkbox, {
            containerClassName: S.fill,
            label: t('visible'),
            eventData: "setVisible",
            value: Model.getVisible(value),
            onChange: onModelChange
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          fillCell: true,
          label: /*#__PURE__*/_jsx(Checkbox, {
            containerClassName: S.fill,
            label: t('shouldCluster'),
            eventData: "setShouldCluster",
            value: Model.getShouldCluster(value),
            onChange: onModelChange
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('showZoom'),
          children: /*#__PURE__*/_jsx(NumericInput, {
            eventData: "setShowAtZoom",
            min: 0,
            max: Math.min(25, Model.getShowAtZoom(value)),
            allowError: true,
            value: Model.getShowAtZoom(value),
            onChange: onModelChange
          }, `haz-${Model.getShowAtZoom(value) || 'n'}`)
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('hideZoom'),
          children: /*#__PURE__*/_jsx(NumericInput, {
            min: Math.max(0, Model.getHideAtZoom(value)),
            max: 25,
            eventData: "setHideAtZoom",
            allowError: true,
            value: Model.getHideAtZoom(value),
            onChange: onModelChange
          }, `saz-${Model.getHideAtZoom(value) || 'n'}`)
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          children: /*#__PURE__*/_jsx(Popup, {
            title: t('help'),
            icon: tIcon('helpIcon', 'fa fa-question-circle fa-fw'),
            children: /*#__PURE__*/_jsxs("div", {
              className: S.pad,
              children: [/*#__PURE__*/_jsx(Text, {
                className: classnames(S.fill, S.hint),
                variant: "caption",
                children: t('settingZoomDescription')
              }), /*#__PURE__*/_jsx(Text, {
                className: S.fill,
                variant: "subtitle",
                children: t('zoomLevel')
              }), /*#__PURE__*/_jsxs("div", {
                className: S.grid,
                style: {
                  columnGap: 'var( --size-padXLarge, 32px)'
                },
                children: [/*#__PURE__*/_jsx("span", {
                  children: "0"
                }), /*#__PURE__*/_jsx("span", {
                  children: t('0Zoom')
                }), /*#__PURE__*/_jsx("span", {
                  children: "3"
                }), /*#__PURE__*/_jsx("span", {
                  children: t('3Zoom')
                }), /*#__PURE__*/_jsx("span", {
                  children: "4"
                }), /*#__PURE__*/_jsx("span", {
                  children: t('4Zoom')
                }), /*#__PURE__*/_jsx("span", {
                  children: "6"
                }), /*#__PURE__*/_jsx("span", {
                  children: t('6Zoom')
                }), /*#__PURE__*/_jsx("span", {
                  children: "10"
                }), /*#__PURE__*/_jsx("span", {
                  children: t('10Zoom')
                }), /*#__PURE__*/_jsx("span", {
                  children: "15"
                }), /*#__PURE__*/_jsx("span", {
                  children: t('15Zoom')
                }), /*#__PURE__*/_jsx("span", {
                  children: "18"
                }), /*#__PURE__*/_jsx("span", {
                  children: t('18Zoom')
                })]
              })]
            })
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Subheader, {
          title: t('interaction')
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          fillCell: true,
          children: /*#__PURE__*/_jsx(Checkbox, {
            label: t('rollover'),
            eventData: "setRolloverable",
            value: Model.getRolloverable(value),
            onChange: onModelChange
          })
        })]
      })
    }), /*#__PURE__*/_jsx(TabPane.Tab, {
      tabName: t('appearance'),
      tabId: "app",
      children: /*#__PURE__*/_jsxs(PropertyGrid, {
        children: [/*#__PURE__*/_jsx(PropertyGrid.Subheader, {
          title: t('fill')
        }), ModelTypeSelector.createSelectorAndEditor({
          editorProps: {
            ...props,
            objectType
          },
          modelKey: 'colorizer',
          isPropertyGrid: true,
          values: Colorizers.getColorizersFor(objectType),
          value: Model.getFillColor(value),
          eventData: 'setFillColor',
          label: t('color'),
          onChange: onModelChange
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('opacity'),
          children: /*#__PURE__*/_jsx(NumericInput, {
            min: 0,
            max: 1,
            float: true,
            eventData: "setFillOpacity",
            allowError: true,
            value: Model.getFillOpacity(value),
            onChange: onModelChange
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Subheader, {
          title: t('outline')
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('width'),
          children: /*#__PURE__*/_jsx(NumericInput, {
            min: 0,
            max: 50,
            float: true,
            eventData: "setLineWidth",
            allowError: true,
            value: Model.getLineWidth(value),
            onChange: onModelChange
          })
        }), ModelTypeSelector.createSelectorAndEditor({
          editorProps: {
            ...props,
            objectType
          },
          modelKey: 'colorizer',
          isPropertyGrid: true,
          values: Colorizers.getColorizersFor(objectType),
          value: Model.getLineColor(value),
          eventData: 'setLineColor',
          label: t('color'),
          onChange: onModelChange
        }), /*#__PURE__*/_jsx(PropertyGrid.Subheader, {
          title: t('label')
        }), ModelTypeSelector.createSelectorAndEditor({
          editorProps: {
            ...props,
            objectType
          },
          modelKey: 'colorizer',
          isPropertyGrid: true,
          label: t('color'),
          values: Colorizers.getColorizersFor(objectType),
          value: Model.getLabelColor(value),
          eventData: 'setLabelColor',
          onChange: onModelChange
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('size'),
          children: /*#__PURE__*/_jsx(NumericInput, {
            min: 0,
            max: 50,
            float: true,
            eventData: "setLabelSize",
            allowError: true,
            value: Model.getLabelSize(value),
            onChange: onModelChange
          })
        })]
      })
    }), /*#__PURE__*/_jsx(TabPane.Tab, {
      tabName: t('rollover'),
      tabId: "rollover",
      children: conditionAppearance('Rollover', onModelChange, value, objectType, props)
    })]
  });
}
function conditionAppearance(prefix, onModelChange, value, objectType, props) {
  const omc = onModelChange;
  const shfc = `setHas${prefix}FillColor`;
  const shfo = `setHas${prefix}FillOpacity`;
  const shlw = `setHas${prefix}LineWidth`;
  const shlc = `setHas${prefix}LineColor`;
  const sfc = `set${prefix}FillColor`;
  const sfo = `set${prefix}FillOpacity`;
  const slw = `set${prefix}LineWidth`;
  const slc = `set${prefix}LineColor`;
  const gfc = Model[`get${prefix}FillColor`](value);
  const gfo = Model[`get${prefix}FillOpacity`](value);
  const glw = Model[`get${prefix}LineWidth`](value);
  const glc = Model[`get${prefix}LineColor`](value);
  const hfc = Model[`getHas${prefix}FillColor`](value);
  const hfo = Model[`getHas${prefix}FillOpacity`](value);
  const hlw = Model[`getHas${prefix}LineWidth`](value);
  const hlc = Model[`getHas${prefix}LineColor`](value);
  return /*#__PURE__*/_jsxs(PropertyGrid, {
    children: [/*#__PURE__*/_jsx(PropertyGrid.Subheader, {
      title: t('fill')
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      fillCell: true,
      children: /*#__PURE__*/_jsx(Checkbox, {
        label: t('color'),
        value: hfc,
        onChange: omc,
        eventData: shfc
      })
    }), !hfc ? null : ModelTypeSelector.createSelectorAndEditor({
      editorProps: {
        ...props,
        objectType
      },
      modelKey: 'colorizer',
      isPropertyGrid: true,
      values: Colorizers.getColorizersFor(objectType),
      value: gfc,
      eventData: sfc,
      onChange: omc
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      label: /*#__PURE__*/_jsx(Checkbox, {
        label: t('opacity'),
        value: hfo,
        onChange: omc,
        eventData: shfo
      }),
      children: hfo && /*#__PURE__*/_jsx(NumericInput, {
        min: 0,
        max: 1,
        float: true,
        eventData: sfo,
        allowError: true,
        value: gfo,
        onChange: omc
      })
    }), /*#__PURE__*/_jsx(PropertyGrid.Subheader, {
      title: t('outline')
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      label: /*#__PURE__*/_jsx(Checkbox, {
        label: "Width",
        value: hlw,
        onChange: omc,
        eventData: shlw
      }),
      children: hlw && /*#__PURE__*/_jsx(NumericInput, {
        min: 0,
        max: 50,
        float: true,
        eventData: slw,
        allowError: true,
        value: glw,
        onChange: omc
      })
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      fillCell: true,
      children: /*#__PURE__*/_jsx(Checkbox, {
        label: t('color'),
        value: hlc,
        onChange: omc,
        eventData: shlc
      })
    }), hlc && ModelTypeSelector.createSelectorAndEditor({
      editorProps: {
        ...props,
        objectType
      },
      modelKey: 'colorizer',
      values: Colorizers.getColorizersFor(objectType),
      value: glc,
      isPropertyGrid: true,
      eventData: slc,
      onChange: omc
    })]
  });
}